import { Cohort } from '@parsleyai/billing'
import { BILLING_BANNER_STATES, BillingBanner, type BillingBannerLabels } from '@parsleyai/ui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIsMobile } from '@/lib/useIsMobile'
import { bannerCountdownDays } from './billingBannerState'
import { useBillingBanner } from './useBillingBanner'

/** Subscription actions have one home; the banner points at it rather than acting itself. */
const SUBSCRIPTION_PAGE = '/settings/subscription'

/**
 * The app-wide billing banner, mounted once in `PageLayout`. `useBillingBanner` decides what to show.
 * A signpost, not a control: every state routes to {@link SUBSCRIPTION_PAGE} and it never calls Stripe.
 */
export function AppBillingBanner({ className }: { className?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { state: resolved, cardState, sub, nowMs } = useBillingBanner()
  const { dismissed, dismiss } = useDismissal(resolved)
  // A `max-w-md` card adrift in a 1440px viewport reads as a stray notification rather than
  // something about this account, so desktop gets the one-line arrangement at the width of the
  // page's own content column. Same hook the rest of the app chrome uses to pick a treatment.
  const isMobile = useIsMobile()

  // Silent on the subscription page it points at. Resolved to `none` rather than unmounting: the DS
  // component owns a height 0↔auto animation and can only run it while it stays mounted.
  const state = dismissed || pathname === SUBSCRIPTION_PAGE ? 'none' : resolved
  const silent = state === 'none'

  // 3D Secure is not a card problem — the card works and the bank wants the user to confirm. Same
  // distinction the settings page draws; telling this user to replace a working card sends
  // them to hit the identical wall with the new one. The banner only picks the words now; the page
  // it lands on picks Portal vs Checkout off the same signal.
  const auth = sub?.paymentFailure?.requiresAction === true
  const days = bannerCountdownDays(state, sub, nowMs)
  const hasCard = sub?.paymentMethod != null

  const goSubscription = () => navigate(SUBSCRIPTION_PAGE)

  // Nothing renders while silent, so resolving copy would only fire missing-key lookups for a
  // deck that doesn't exist (`billing.banner.trial.none.*`).
  const labels: BillingBannerLabels = silent ? { title: '' } : { ...copy(), dismiss: t('billing.banner.dismiss') }
  if (!silent && days !== null) labels.countdown = countdown()

  function countdown(): string {
    if (state === 'trial_t0') return t('billing.banner.countdown.today')
    if (days === 1) return t('billing.banner.countdown.lastDay')
    return t('billing.banner.countdown.days', { count: days ?? 0 })
  }

  function copy(): BillingBannerLabels {
    switch (state) {
      case 'past_due':
      case 'grace_today': {
        const deck = `${state === 'past_due' ? 'pastDue' : 'graceToday'}${auth ? 'Auth' : ''}`
        return { title: t(`billing.banner.${deck}.title`), body: t(`billing.banner.${deck}.body`) }
      }
      case 'locked': {
        // Two ways to be locked, and only one of them is fixable with a card. `terminal` means
        // dunning ran out and the subscription is gone — that's a restart, not a repair.
        const deck = cardState === 'terminal' ? 'lockedTerminal' : 'locked'
        return { title: t(`billing.banner.${deck}.title`), body: t(`billing.banner.${deck}.body`) }
      }
      case 'paused': {
        // "Add a card" is the wrong instruction for someone who already has one.
        const deck = hasCard ? 'pausedWithCard' : 'paused'
        return { title: t(`billing.banner.${deck}.title`), body: t(`billing.banner.${deck}.body`) }
      }
      case 'recovered':
        return { title: t('billing.banner.recovered.title') }
      default: {
        // Beta and trial are the same Stripe status framed completely differently: one sells the
        // product, the other protects six months of accumulated cooking.
        const deck = sub?.cohort === Cohort.Beta ? 'beta' : 'trial'
        const step = state.replace('trial_', '')
        const end = sub?.trialEnd?.toMillis()
        return {
          title: t(`billing.banner.${deck}.${step}.title`, {
            weekday: end ? new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(end) : ''
          }),
          body: t(`billing.banner.${deck}.${step}.body`)
        }
      }
    }
  }

  // State picks the words; the destination is fixed, so no state can route somewhere else.
  // `null` for the two states with nothing to act on.
  const cta = (() => {
    switch (state) {
      case 'none':
      case 'recovered':
        return null
      case 'past_due':
      case 'grace_today':
        return auth ? 'confirmPayment' : 'updateCard'
      case 'locked':
        return cardState === 'terminal' ? 'restart' : 'updateCard'
      case 'paused':
        return hasCard ? 'choosePlan' : 'addCard'
      default:
        return 'addCard'
    }
  })()

  const action = cta ? { label: t(`billing.settings.cta.${cta}`), onClick: goSubscription } : undefined

  return (
    <BillingBanner
      state={state}
      labels={labels}
      action={action}
      onDismiss={dismiss}
      layout={isMobile ? 'stacked' : 'row'}
      // The app's content column, so the banner's edges line up with the page beneath it
      // rather than floating over it.
      className={className ?? 'mx-auto w-full max-w-page'}
    />
  )
}

const DISMISS_KEY = 'parsley.billingBanner.dismissed'

/**
 * Remember a dismissal, keyed by the state dismissed — waving away "7 days left" must not suppress
 * "3 days left". Persisted, not session-scoped, so a reload doesn't re-nag.
 */
function useDismissal(state: string) {
  const [dismissedState, setDismissedState] = useState(() => read())
  const config = BILLING_BANNER_STATES[state as keyof typeof BILLING_BANNER_STATES]

  return {
    dismissed: config?.dismissible === true && dismissedState === state,
    dismiss: () => {
      write(state)
      setDismissedState(state)
    }
  }
}

// Storage throws in Safari's private mode and when a user blocks it outright. This is app chrome —
// mounted on every authenticated page — so an unguarded read would take down the whole app rather
// than one screen. Forgetting a dismissal is the correct way to fail here.
function read(): string | null {
  try {
    return window.localStorage.getItem(DISMISS_KEY)
  } catch {
    return null
  }
}

function write(value: string): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, value)
  } catch {
    // Dismissal stays in React state for this session; it just won't survive a reload.
  }
}
